import {useCallback, useMemo} from 'react';
import {useGridApiRef} from '@mui/x-data-grid-pro';
import type {Data} from 'schemaDefinition/types';
import {useGetResourceListByIds} from 'api/hooks/useGetResourceListByIds';
import {useDataGridSettings} from 'services/settings/hooks';
import {getOffset} from 'services/utils/getOffset';
import {useDataTablePagination} from 'components/dataTable/hooks';

/**
 * Loads data for current page of ids. Returns data together with Table props
 * for stored settings and pagination.
 */
export function useDataByIdPagination<TData extends Data = Data>(
  schemaName: string,
  ids: string[],
  loader: (ids: string[]) => Promise<TData[]>,
) {
  const apiRef = useGridApiRef();
  // Ids differ per bulk update, so the page is not restored
  const {initialState} = useDataGridSettings(schemaName);
  const {paginationModel, onPaginationModelChange} = useDataTablePagination(
    apiRef,
    initialState?.pagination?.paginationModel,
  );

  const itemIds = useMemo(() => {
    const offset = getOffset(paginationModel.pageSize, paginationModel.page);
    return ids.slice(offset, offset + paginationModel.pageSize);
  }, [ids, paginationModel.page, paginationModel.pageSize]);

  const load = useCallback(
    (pageIds: string[]) => {
      return pageIds.length ? loader(pageIds) : Promise.reject();
    },
    [loader],
  );

  const {data, status} = useGetResourceListByIds<TData>(itemIds, load);

  return {
    data: data ?? [],
    status,
    apiRef,
    initialState,
    onPaginationModelChange,
  };
}
